import { commonAPI } from '@/services-ts/axios'
import type {
  IAnswerWinPayload,
  IGetAnswerWinQuestionList,
  IPaging,
  IUpdateAnswerWinQuestion,
  IUpdateAnswerWinQuestionImage,
} from '@/types/Request'
import type { IAnswerWinStore } from '@/types/Store/State'
import { Alert, Format } from '@/utils-ts'
import { defineStore } from 'pinia'

export const useAnswerWinStore = defineStore('answerWin', {
  state: (): IAnswerWinStore => ({
    answerWinList: {
      data: [],
      total: 0,
    },
    answerWinQuestionList: {
      data: [],
      total: 0,
    },
  }),

  actions: {
    async GetAnswerWinList(payload: IPaging) {
      const { status, data, total } = await commonAPI.AnswerWinServices.GetAnswerWinList(payload)
      if (status) {
        this.answerWinList = { data: data || [], total: total || 0 }
      }
    },

    async CreateAnswerWin(payload: IAnswerWinPayload) {
      const { status } = await commonAPI.AnswerWinServices.CreateAnswerWin(payload)

      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async UpdateAnswerWin(payload: IAnswerWinPayload) {
      const { status } = await commonAPI.AnswerWinServices.UpdateAnswerWin(payload)
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async DeleteAnswerWin(Id: string) {
      const { status } = await commonAPI.AnswerWinServices.DeleteAnswerWin(Id)
      if (status) {
        this.answerWinList.data = this.answerWinList.data.filter(item => item.id !== Id)
        void Alert({ message: Format('admin_global_deleted'), type: 'success' })
      }
      return status
    },

    async GetAnswerWinQuestionList(payload: IGetAnswerWinQuestionList) {
      const { status, data, total } =
        await commonAPI.AnswerWinServices.GetAnswerWinQuestionList(payload)
      if (status) {
        this.answerWinQuestionList = { data: data || [], total: total || 0 }
      }
    },

    async UpdateAnswerWinQuestion(payload: IUpdateAnswerWinQuestion, isEditable: boolean) {
      const image = payload.Image
      delete payload.Image

      const { status, data } = await commonAPI.AnswerWinServices.UpdateAnswerWinQuestion(payload)
      if (status) {
        const result = data
        const index = this.answerWinQuestionList.data.findIndex(item => item.id === result.id)
        if (index === -1) {
          result.answers.sort((a, b) => a.order - b.order)
          this.answerWinQuestionList.data.push({ ...result, isEditable })
        } else {
          this.answerWinQuestionList.data[index] = {
            ...result,
            isEditable: isEditable,
          }
        }
        this.answerWinQuestionList.data.sort((a, b) => a.order - b.order)

        if (isEditable && image && image instanceof Blob) {
          await this.UpdateAnswerWinQuestionImage({
            AnswerWinQuestionId: result.id,
            AnswerWinId: payload.AnswerWinId,
            Image: image,
            ImageUrl: result.imageUrl,
          })
        }

        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async DeleteAnswerWinQuestion(Id: string) {
      const { status } = await commonAPI.AnswerWinServices.DeleteAnswerWinQuestion(Id)
      if (status) {
        this.answerWinQuestionList.data = this.answerWinQuestionList.data.filter(
          item => item.id !== Id,
        )
        this.answerWinQuestionList.total -= 1
        void Alert({ message: Format('admin_global_deleted'), type: 'success' })
      }
      return status
    },

    async UpdateAnswerWinQuestionImage(data: IUpdateAnswerWinQuestionImage) {
      const { status } = await commonAPI.AnswerWinServices.UpdateAnswerWinQuestionImage(data)
      if (status) {
        const item = this.answerWinQuestionList.data.find(
          item => item.id === data.AnswerWinQuestionId,
        )
        if (item) {
          item.imageUrl = URL.createObjectURL(data.Image)
        }
      }
    },

    async UpdateAnswerWinQuestionOrder(payload: {
      AnswerWinQuestionOrderModel: { Order: number; AnswerWinQuestionId: string }[]
    }) {
      const { status } = await commonAPI.AnswerWinServices.UpdateAnswerWinQuestionOrder(payload)
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async CloneAnswerWin(AnswerWinId: string) {
      const { status } = await commonAPI.AnswerWinServices.CloneAnswerWin(AnswerWinId)
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async ResetAnswerWin(AnswerWinId: string) {
      const { status } = await commonAPI.AnswerWinServices.ResetAnswerWin(AnswerWinId)
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },
  },
})
